import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatInteger } from '@brimo/shared';
import { Button, Dialog, Pill, Skeleton } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { api, errorMessage } from '../../../lib/api';
import { organizationKeys, uploadWithProgress } from './api';
import type { LegalTextDetail, LegalTextItem, LegalTexts } from './types';
import styles from './LegalParts.module.css';

type Variant = 'wizard' | 'settings';

/** Upload of a legal PDF (`POST …/legal-texts/:type/pdf`) through a hidden file input. */
function usePdfUpload(type: string, onDone: (texts: LegalTexts) => void) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function upload(file: File) {
    setError(null);
    setProgress(0);
    const form = new FormData();
    form.append('file', file);
    try {
      const next = await uploadWithProgress<LegalTexts>(
        `/settings/organization/legal-texts/${type}/pdf`,
        form,
        setProgress,
      );
      onDone(next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : errorMessage(failure));
    } finally {
      setProgress(null);
    }
  }
  const element = (
    <input
      ref={input}
      type="file"
      accept="application/pdf,.pdf"
      className={styles.fileInput}
      tabIndex={-1}
      aria-hidden="true"
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) void upload(file);
      }}
    />
  );
  return { open: () => input.current?.click(), element, progress, error };
}

export interface LegalTextListProps {
  texts: LegalTexts;
  variant: Variant;
  /** Type whose inline editor is open (wizard) — the settings page opens the editor as a dialog. */
  editing?: string | null | undefined;
  onEdit: (type: string | null) => void;
  onChanged?: (() => void) | undefined;
}

/** The four legal text cards in their states (text/PDF present, editor open, missing with warning). */
export function LegalTextList({ texts, variant, editing, onEdit, onChanged }: LegalTextListProps) {
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<LegalTextItem | null>(null);
  function accept(next: LegalTexts) {
    queryClient.setQueryData(organizationKeys.legalTexts, next);
    void queryClient.invalidateQueries({ queryKey: ['organization', 'legal-text'] });
    onChanged?.();
  }
  return (
    <>
      <div className={clsx(styles.list, variant === 'settings' && styles.listSettings)}>
        {texts.items.map((item) =>
          variant === 'wizard' && editing === item.type ? (
            <InlineEditor
              key={item.type}
              item={item}
              maxCharacters={texts.maxCharacters}
              onSaved={(next) => {
                accept(next);
                onEdit(null);
              }}
            />
          ) : (
            <LegalCard
              key={item.type}
              item={item}
              variant={variant}
              onView={() => setViewing(item)}
              onEdit={() => onEdit(item.type)}
              onUploaded={accept}
            />
          ),
        )}
      </div>
      <LegalViewDialog item={viewing} onClose={() => setViewing(null)} />
    </>
  );
}

function LegalCard({
  item,
  variant,
  onView,
  onEdit,
  onUploaded,
}: {
  item: LegalTextItem;
  variant: Variant;
  onView: () => void;
  onEdit: () => void;
  onUploaded: (texts: LegalTexts) => void;
}) {
  const pdf = usePdfUpload(item.type, onUploaded);
  const present = item.source !== null;
  const meta =
    pdf.progress !== null
      ? `Wird hochgeladen … ${Math.round(pdf.progress * 100)} %`
      : present
        ? item.meta
        : item.missingText;
  return (
    <article className={clsx(styles.card, !present && styles.cardMissing)} aria-label={item.title}>
      <span className={clsx(styles.icon, !present && styles.iconMissing)} aria-hidden="true">
        {present ? '✓' : '!'}
      </span>
      <div className={styles.text}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{item.title}</h3>
          {item.example ? <Pill variant="warning">Beispieltext</Pill> : null}
        </div>
        <span className={styles.meta}>{meta}</span>
        {pdf.error ? (
          <span className={styles.error} role="alert">
            {pdf.error}
          </span>
        ) : null}
      </div>
      <span className={styles.actions}>
        {present ? (
          <>
            <button type="button" className={clsx('pressable', styles.action)} onClick={onView}>
              Ansehen
            </button>
            <button
              type="button"
              className={clsx('pressable', styles.action, styles.actionQuiet)}
              aria-label={`${item.title} ersetzen`}
              onClick={item.source === 'pdf' ? pdf.open : onEdit}
            >
              Ersetzen
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={clsx('pressable', styles.action, variant === 'settings' && styles.actionPrimary)}
              aria-label={`${item.title}: Text einfügen`}
              onClick={onEdit}
            >
              Text einfügen
            </button>
            <button
              type="button"
              className={clsx('pressable', styles.action)}
              aria-label={`${item.title}: PDF hochladen`}
              onClick={pdf.open}
            >
              PDF hochladen
            </button>
          </>
        )}
      </span>
      {pdf.element}
    </article>
  );
}

/** The current text of a type (for „Ersetzen" and „Ansehen"). */
function useLegalText(type: string | null, enabled: boolean) {
  return useQuery({
    queryKey: organizationKeys.legalText(type ?? ''),
    queryFn: () => api<LegalTextDetail>(`/settings/organization/legal-texts/${type ?? ''}`),
    enabled: enabled && type !== null,
  });
}

function EditorBody({
  item,
  maxCharacters,
  value,
  onChange,
  error,
  dialog,
}: {
  item: LegalTextItem;
  maxCharacters: number;
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  dialog: boolean;
}) {
  const over = value.length > maxCharacters;
  return (
    <>
      <textarea
        className={clsx(
          styles.textarea,
          dialog && styles.textareaDialog,
          (over || error) && styles.textareaInvalid,
        )}
        aria-label={`${item.title} · Text`}
        value={value}
        autoFocus
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}

function useEditor(item: LegalTextItem, onSaved: (texts: LegalTexts) => void) {
  const existing = useLegalText(item.type, item.source === 'text');
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const text = value ?? (item.source === 'text' ? (existing.data?.content ?? '') : '');
  const loading = item.source === 'text' && existing.isPending && value === null;
  async function save() {
    setSaving(true);
    setError(null);
    try {
      const next = await api<LegalTexts>(`/settings/organization/legal-texts/${item.type}`, {
        method: 'PUT',
        body: { content: text },
      });
      onSaved(next);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  }
  return { text, setValue, error, saving, loading, save };
}

function InlineEditor({
  item,
  maxCharacters,
  onSaved,
}: {
  item: LegalTextItem;
  maxCharacters: number;
  onSaved: (texts: LegalTexts) => void;
}) {
  const editor = useEditor(item, onSaved);
  const pdf = usePdfUpload(item.type, onSaved);
  const over = editor.text.length > maxCharacters;
  return (
    <article className={clsx(styles.card, styles.cardEditing)} aria-label={item.title}>
      <span className={clsx(styles.icon, styles.iconEditing)} aria-hidden="true">
        ✎
      </span>
      <div className={styles.editor}>
        <div className={styles.editorHead}>
          <h3 className={styles.title}>{item.title}</h3>
          <span className={styles.editorHint}>Text einfügen · Formatierung wird übernommen</span>
        </div>
        {editor.loading ? (
          <Skeleton variant="block" height={120} />
        ) : (
          <EditorBody
            item={item}
            maxCharacters={maxCharacters}
            value={editor.text}
            onChange={editor.setValue}
            error={editor.error ?? pdf.error}
            dialog={false}
          />
        )}
        <div className={styles.editorFoot}>
          <span className={clsx(styles.counter, over && styles.counterOver)}>
            {formatInteger(editor.text.length)} von {formatInteger(maxCharacters)} Zeichen
          </span>
          <button type="button" className={clsx('pressable', styles.action, styles.push)} onClick={pdf.open}>
            {pdf.progress !== null
              ? `Wird hochgeladen … ${Math.round(pdf.progress * 100)} %`
              : 'Stattdessen PDF hochladen'}
          </button>
          <button
            type="button"
            className={clsx('pressable', styles.action, styles.actionPrimary)}
            disabled={over || editor.text.trim() === '' || editor.saving}
            aria-busy={editor.saving || undefined}
            onClick={() => void editor.save()}
          >
            Speichern
          </button>
        </div>
      </div>
      {pdf.element}
    </article>
  );
}

export interface LegalEditorDialogProps {
  item: LegalTextItem | null;
  maxCharacters: number;
  onClose: () => void;
  onSaved: (texts: LegalTexts) => void;
}

/** Einstellungen 21 „Text einfügen" / „Ersetzen": the editor with the counter in a dialog. */
export function LegalEditorDialog({ item, maxCharacters, onClose, onSaved }: LegalEditorDialogProps) {
  if (item === null) return null;
  return <EditorDialog item={item} maxCharacters={maxCharacters} onClose={onClose} onSaved={onSaved} />;
}

function EditorDialog({
  item,
  maxCharacters,
  onClose,
  onSaved,
}: {
  item: LegalTextItem;
  maxCharacters: number;
  onClose: () => void;
  onSaved: (texts: LegalTexts) => void;
}) {
  const editor = useEditor(item, onSaved);
  const over = editor.text.length > maxCharacters;
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Rechtstexte"
      title={item.title}
      subtitle="Text einfügen · Formatierung wird übernommen"
      width={720}
      footerNote={
        <span className={clsx(styles.counter, over && styles.counterOver)}>
          {formatInteger(editor.text.length)} von {formatInteger(maxCharacters)} Zeichen
        </span>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            loading={editor.saving}
            disabled={over || editor.text.trim() === ''}
            onClick={() => void editor.save()}
          >
            Speichern
          </Button>
        </>
      }
    >
      {editor.loading ? (
        <Skeleton variant="block" height={320} />
      ) : (
        <EditorBody
          item={item}
          maxCharacters={maxCharacters}
          value={editor.text}
          onChange={editor.setValue}
          error={editor.error}
          dialog
        />
      )}
    </Dialog>
  );
}

/** „Ansehen": the pasted text, or the PDF inline with a download link. */
export function LegalViewDialog({ item, onClose }: { item: LegalTextItem | null; onClose: () => void }) {
  const detail = useLegalText(item?.type ?? null, item !== null);
  return (
    <Dialog
      open={item !== null}
      onClose={onClose}
      eyebrow="Rechtstexte"
      title={item?.title ?? ''}
      width={760}
    >
      {detail.isPending ? (
        <Skeleton variant="block" height={280} />
      ) : detail.isError ? (
        <span className={styles.error} role="alert">
          {errorMessage(detail.error)}
        </span>
      ) : detail.data.source === 'pdf' && detail.data.file ? (
        <>
          <object
            className={styles.pdf}
            data={detail.data.file.url}
            type="application/pdf"
            aria-label={item?.title}
          >
            <a href={detail.data.file.url} download={detail.data.file.name}>
              {detail.data.file.name} herunterladen
            </a>
          </object>
          <span className={styles.viewerMeta}>
            <a href={detail.data.file.url} download={detail.data.file.name}>
              {detail.data.file.name} herunterladen
            </a>
          </span>
        </>
      ) : (
        <div className={styles.viewer}>{detail.data.content}</div>
      )}
    </Dialog>
  );
}
